import React, { useEffect, useState } from "react";
import axios from "axios";
import { API, fileUrl } from "@/lib/api";
import { ArrowUpRight, X } from "lucide-react";

export default function Portfolio() {
    const [projects, setProjects] = useState(null); // null = loading, [] = loaded empty
    const [active, setActive] = useState(null);

    useEffect(() => {
        axios
            .get(`${API}/portfolio`)
            .then((r) => setProjects(r.data || []))
            .catch(() => setProjects([]));
    }, []);

    const items = (projects || []).map((p) => ({
        ...p,
        cover_url: p.cover_image ? fileUrl(p.cover_image) : null,
    }));

    // Adaptive layouts so the grid never leaves obvious empty cells
    const layoutsByCount = {
        1: ["md:col-span-12 md:row-span-2"],
        2: ["md:col-span-7 md:row-span-2", "md:col-span-5 md:row-span-2"],
        3: [
            "md:col-span-8 md:row-span-2",
            "md:col-span-4 md:row-span-1",
            "md:col-span-4 md:row-span-1",
        ],
        4: [
            "md:col-span-6 md:row-span-2",
            "md:col-span-6 md:row-span-1",
            "md:col-span-3 md:row-span-1",
            "md:col-span-3 md:row-span-1",
        ],
        5: [
            "md:col-span-7 md:row-span-2",
            "md:col-span-5 md:row-span-1",
            "md:col-span-5 md:row-span-1",
            "md:col-span-6 md:row-span-1",
            "md:col-span-6 md:row-span-1",
        ],
    };
    const defaultLayout = [
        "md:col-span-7 md:row-span-2",
        "md:col-span-5 md:row-span-1",
        "md:col-span-5 md:row-span-1",
        "md:col-span-4 md:row-span-1",
        "md:col-span-4 md:row-span-1",
        "md:col-span-4 md:row-span-1",
    ];
    const layoutClasses = layoutsByCount[items.length] || defaultLayout;

    const isLoading = projects === null;
    const isEmpty = !isLoading && items.length === 0;

    return (
        <section
            id="portfolio"
            data-testid="portfolio-section"
            className="section-pad"
        >
            <div className="container-edge">
                <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
                    <h2
                        data-testid="portfolio-heading"
                        className="font-display text-4xl md:text-5xl lg:text-6xl tracking-tight font-medium leading-[1.02] text-balance"
                    >
                        Selected{" "}
                        <span className="italic font-normal text-champagne">
                            work examples.
                        </span>
                    </h2>
                    <p className="max-w-md text-foreground/70 text-[15px] leading-relaxed">
                        A growing set of projects across event bars, activations,
                        private events, stage sets, launches and venue
                        transformations.
                    </p>
                </div>

                {isLoading ? null : isEmpty ? (
                    <div
                        data-testid="portfolio-empty"
                        className="mt-16 border border-border py-24 px-8 text-center"
                    >
                        <p className="overline">In progress</p>
                        <p className="mt-4 font-display text-2xl md:text-3xl tracking-tight text-foreground/70 max-w-xl mx-auto">
                            Case studies are being added.
                        </p>
                        <p className="mt-3 text-[14px] text-foreground/55 max-w-md mx-auto">
                            Project visuals, technical drawings and write-ups will
                            appear here as each piece is published.
                        </p>
                        <a href="#contact" className="btn-primary mt-8">
                            Start a Project
                        </a>
                    </div>
                ) : (
                    <div className="mt-16 grid grid-cols-1 md:grid-cols-12 md:auto-rows-[280px] gap-px bg-border">
                        {items.slice(0, 6).map((p, i) => (
                            <button
                                key={p.id || p.slug || i}
                                data-testid={`portfolio-card-${i}`}
                                onClick={() => setActive(p)}
                                className={`${layoutClasses[i] || "md:col-span-4 md:row-span-1"} group bg-background relative overflow-hidden text-left min-h-[280px]`}
                            >
                                {p.cover_url ? (
                                    <img
                                        src={p.cover_url}
                                        alt={p.title}
                                        className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.04]"
                                    />
                                ) : (
                                    <div className="absolute inset-0 bg-foreground/10" />
                                )}
                                <div className="absolute inset-0 bg-gradient-to-t from-foreground/85 via-foreground/10 to-transparent opacity-90 group-hover:opacity-95 transition-opacity" />
                                <div className="relative h-full flex flex-col justify-between p-6 md:p-8 text-background">
                                    <div className="flex items-start justify-between">
                                        <span className="overline !text-background/70">
                                            {p.project_type || "Project"}
                                        </span>
                                        <ArrowUpRight
                                            size={18}
                                            className="opacity-0 group-hover:opacity-100 transition-opacity"
                                        />
                                    </div>
                                    <div>
                                        <h3 className="font-display text-2xl md:text-3xl font-medium tracking-tight">
                                            {p.title}
                                        </h3>
                                        <p className="mt-2 text-background/80 text-[13.5px] leading-relaxed max-w-md line-clamp-2">
                                            {p.short_description}
                                        </p>
                                    </div>
                                </div>
                            </button>
                        ))}
                    </div>
                )}
            </div>

            {/* Project modal */}
            {active && (
                <div
                    data-testid="portfolio-modal"
                    className="fixed inset-0 z-[80] bg-navy-deep/85 backdrop-blur-sm flex items-start justify-center overflow-y-auto py-12 px-4"
                    onClick={() => setActive(null)}
                >
                    <div
                        className="relative bg-background w-full max-w-5xl"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <button
                            onClick={() => setActive(null)}
                            data-testid="portfolio-modal-close"
                            className="absolute top-4 right-4 z-10 p-2 bg-background/80 hover:bg-foreground hover:text-background transition-colors"
                            aria-label="Close"
                        >
                            <X size={18} />
                        </button>
                        {active.cover_url && (
                            <img
                                src={active.cover_url}
                                alt={active.title}
                                className="w-full aspect-[16/9] object-cover"
                            />
                        )}
                        <div className="p-6 md:p-10">
                            <p className="overline">{active.project_type}</p>
                            <h3 className="mt-3 font-display text-3xl md:text-4xl font-medium tracking-tight">
                                {active.title}
                            </h3>
                            <div className="mt-5 text-foreground/75 text-[15px] leading-relaxed max-w-2xl space-y-4">
                                {(active.long_description || active.short_description || "")
                                    .split(/\n{2,}/)
                                    .filter(Boolean)
                                    .map((para, idx) => (
                                        <p key={idx}>{para}</p>
                                    ))}
                            </div>
                            {active.client_name || active.location || active.year ? (
                                <div className="mt-6 grid grid-cols-2 md:grid-cols-3 gap-6 border-y border-border py-6 text-[13px]">
                                    {active.client_name && (
                                        <div>
                                            <p className="overline">Client</p>
                                            <p className="mt-2 text-foreground/80">
                                                {active.client_name}
                                            </p>
                                        </div>
                                    )}
                                    {active.location && (
                                        <div>
                                            <p className="overline">Location</p>
                                            <p className="mt-2 text-foreground/80">
                                                {active.location}
                                            </p>
                                        </div>
                                    )}
                                    {active.year && (
                                        <div>
                                            <p className="overline">Year</p>
                                            <p className="mt-2 text-foreground/80">
                                                {active.year}
                                            </p>
                                        </div>
                                    )}
                                </div>
                            ) : null}
                            {active.services_used?.length ? (
                                <div className="mt-6">
                                    <p className="overline">Services used</p>
                                    <div className="mt-3 flex flex-wrap gap-2">
                                        {active.services_used.map((s) => (
                                            <span
                                                key={s}
                                                className="text-[12px] uppercase tracking-[0.1em] border border-border px-3 py-1.5"
                                            >
                                                {s}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            ) : null}
                            {active.gallery?.length ? (
                                <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-2">
                                    {active.gallery.map((g, idx) => {
                                        const isImg =
                                            g.file_kind === "image" ||
                                            (g.content_type || "").startsWith("image/");
                                        return (
                                            <figure key={idx} className="flex flex-col">
                                                {isImg ? (
                                                    <img
                                                        src={fileUrl(g.storage_path)}
                                                        alt={g.caption || `${active.title} ${idx + 1}`}
                                                        className="w-full aspect-[4/3] object-cover bg-foreground/5"
                                                    />
                                                ) : (
                                                    <a
                                                        href={fileUrl(g.storage_path)}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="group/doc relative w-full aspect-[4/3] bg-foreground text-background flex flex-col items-center justify-center gap-3 hover:bg-foreground/85 transition-colors"
                                                    >
                                                        <span className="overline !text-background/60">
                                                            {(g.content_type || "Document")
                                                                .split("/")
                                                                .pop()
                                                                .toUpperCase()}
                                                        </span>
                                                        <span className="font-display text-xl md:text-2xl tracking-tight max-w-[80%] text-center">
                                                            {g.original_filename || "Technical drawing"}
                                                        </span>
                                                        <span className="text-[11px] tracking-[0.18em] uppercase border border-background/40 px-3 py-1.5 mt-2">
                                                            Open file →
                                                        </span>
                                                    </a>
                                                )}
                                                {g.caption && (
                                                    <figcaption className="mt-2 text-[12px] text-foreground/55">
                                                        {g.caption}
                                                    </figcaption>
                                                )}
                                            </figure>
                                        );
                                    })}
                                </div>
                            ) : null}
                        </div>
                    </div>
                </div>
            )}
        </section>
    );
}
